import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isInteger from 'lodash/isInteger';
import { smallTables } from '../data/Constants';
import { craverStaffAddress } from './Functions';

const LEVEL_NAMES = Object.freeze({
  LOCK: 'lock',
  HIDE: 'hide',
  VIEW: 'view',
  EDIT: 'edit',
  DELETE: 'delete',
  ADD: 'add',
  DELETE_AS_LOCATION_ADMIN: 'deleteAsLocationAdmin',
});

export const {
  LOCK,
  HIDE,
  VIEW,
  EDIT,
  DELETE,
  ADD,
  DELETE_AS_LOCATION_ADMIN,
} = LEVEL_NAMES;

const levels = {
  [LOCK]: 0,
  [HIDE]: 1,
  [VIEW]: 2,
  [EDIT]: 3,
  [DELETE]: 3,
  [ADD]: 4,
  [DELETE_AS_LOCATION_ADMIN]: 4,
};

const craverStaffFullPermissionResources = ['orders', 'locations', 'users', 'reports', 'home', 'menu', 'tiers', 'coupons', 'companies'];

const fullAuthorization = {
  locationIds: [],
  action: 'add',
};

const staffFullDefaultAuthorizations = {};

craverStaffFullPermissionResources.forEach(
  resource => staffFullDefaultAuthorizations[resource] = fullAuthorization
);

/**
 * Returns a boolean. Does a attemptedActionIndex <= permittedActionLevelIndex
 * comparison to determine authorization, providing for various exceptions and cases.
 *
 * For normal admins and higher, if you have the 'edit' permission on a resource, you get the
 * 'add' permission on its subResources.
 *
 * The 'edit' permission usually includes deleting, except for locations.
 * Normally, only Craver Staff (@admin.com accounts) should be able to delete locations.
 * You can allow non- @admin.com accounts to add/duplicate/delete locations
 * if you give the 'add' authorization for locations.
 *
 * Craver Staff @admin.com accounts will have full permissions by default
 * if an authorization record does not exist for
 * the resource key getting looked up.
 *
 * On the Menu page, Products, Customizations, Categories, and Taxes
 * are not treated as subResources of Menu. If Menu has the 'edit' permission,
 * each of these also has the 'edit' permission.
 *
 * Location admins get their authorizations for 'menu', 'products',
 * editing product items and (customization) 'options', and 'orders'
 * from the 'locations' key in the authorizations object.
 * It's been decided to not fine-grain this for the moment.
 *
 * For location admins, the 'edit' permission does not include 'delete' in most cases,
 * like it does for normal admins and above.
 * However, Location admins may delete or duplicate delivery and catering zones.
 *
 * @param {*} admin
 * @param {*} resource
 * @param {*} subResourceOrSmallTable
 * @param {*} attemptedAction
 */
const checkIsAuthorized = (admin, resource, subResourceOrSmallTable, attemptedAction) => {
  if (!admin) return false;
  // Authorizations look like {
  //  menu: { action: 'add', locationIds: number[] },
  //  reports: { action: 'lock', locationIds: number[] },
  // }
  const authorizations = getAuthorizations(admin);

  // e.g. 'menu', 'reports', usually the resource.
  // For location admins, many authorizations are covered under the 'locations' key.
  const authorizationLookupName = subPageResources.includes(resource) || getLocationIdOfAuthorizationOrAdmin('locations', admin)
    ? getAuthorizationLookupName(resource, subResourceOrSmallTable, admin)
    : resource;

  // e.g. 'add', 'view', 'edit'
  const permittedActionName = figureOutPermittedActionName(
    authorizations,
    authorizationLookupName,
    resource,
    subResourceOrSmallTable,
    attemptedAction,
    admin,
  );

  const attemptedActionIndex = levels[attemptedAction];
  const permittedActionLevelIndex = levels[permittedActionName];

  if (!isInteger(attemptedActionIndex) || !isInteger(permittedActionLevelIndex)) return false;
  const adminLocationId = getLocationIdFromAuthorizationsInAdmin(admin)
    || getLocationIdFromLocationInAdmin(admin);
  // For non-location admins, e.g. normal admins and Enterprise Tier
  if (!adminLocationId) return attemptedActionIndex <= permittedActionLevelIndex;

  // For location admins. Location admins can view Categories, Customizations, and Taxes
  // on the Menu page, but cannot edit them. They CAN edit customization_options, products,
  // and items - their outOfStock values. Location admin's edit permission does
  // not let them delete these resources, like normal admins can with an 'edit' permission.
  if ((resource === 'menu' && smallTables.includes(subResourceOrSmallTable))
    // Do not let location admins edit items through the old /products/:id/items/:item_id/edit link
    || (subResourceOrSmallTable === 'items')
    || (resource === 'customizations' && !subResourceOrSmallTable)) {
    return attemptedActionIndex <= levels.view;
  }

  if ((resource === 'menu' && subResourceOrSmallTable === 'options')
    || (resource === 'customizations' && subResourceOrSmallTable === 'options')
    || (resource === 'products')) {
    return attemptedActionIndex <= levels.edit;
  }
  return attemptedActionIndex <= permittedActionLevelIndex;
};

/**
 * @param {*} authorizationLookupName e.g. 'locations'.
 * @param {*} admin The admin, for backwards compatibility looking up admin.location.id,
 * the old way to make a location admin
 *
 * For backwards compatibility,
 * whether the location comes from the authorizations table or the admins table
 */
const getLocationIdOfAuthorizationOrAdmin = (authorizationLookupName, admin) => {
  const oldWayLocationId = getLocationIdFromLocationInAdmin(admin);

  const authorizations = getAuthorizations(admin);
  const locationArrayFromAuthorizations = getAuthorizationRecord(
    authorizations,
    authorizationLookupName,
    admin,
  ).locationIds;
  const newWayLocationId = locationArrayFromAuthorizations[0];

  return newWayLocationId || oldWayLocationId;
};

export const checkIsLocationAdmin = (admin) => {
  if (!admin || !admin.email) return false;
  return !!getLocationIdOfAuthorizationOrAdmin('locations', admin);
};

const getLocationIdFromAuthorizationsInAdmin = admin => get(admin, 'authorizations.locations.locationIds[0]', null);
const getLocationIdFromLocationInAdmin = admin => get(admin, 'location.id', null);

const defaultAuthorizations = (admin) => {
  const { email } = admin;
  const isCraverStaff = email && craverStaffAddress.test(email);
  const oldWayLocationId = getLocationIdFromLocationInAdmin(admin);

  // Location admins derive their authorizations for all their resources from the locations key
  const newWayLocationId = getLocationIdFromAuthorizationsInAdmin(admin);
  const finalLocationId = newWayLocationId || oldWayLocationId;
  const isLocationAdmin = !!oldWayLocationId || !!newWayLocationId;

  // For testing, and for accounts in Beta like locationadmin@admin.com
  if (isCraverStaff && isLocationAdmin) return locationAdminDefaultAuthorizations;
  if (isCraverStaff) return staffFullDefaultAuthorizations;
  if (!isLocationAdmin) return normalAdminDefaultAuthorizations;

  // Location Admins
  if (finalLocationId === Number(locationAdminDefaultAuthorizations.locations.locationIds[0])) {
    return locationAdminDefaultAuthorizations;
  }

  // If not yet defined, or just logged in to new account,
  // ensures replacement of location ids if not provided by LOGIN_SUCCESS returned value.
  for (const key in locationAdminDefaultAuthorizations) {
    locationAdminDefaultAuthorizations[key].locationIds = [Number(finalLocationId)];
  }
  return locationAdminDefaultAuthorizations;
};

// The default is a normal admin, higher than a location admin, but lower than Enterprise Tier.
const normalAdminDefaultAuthorizations = {
  home: {
    locationIds: [],
    action: 'view',
  },
  menu: {
    locationIds: [],
    action: 'add',
  },
  tiers: {
    locationIds: [],
    action: 'add',
  },
  orders: {
    locationIds: [],
    action: 'edit',
  },
  locations: {
    locationIds: [],
    action: 'edit',
  },
  users: {
    locationIds: [],
    action: 'edit',
  },
  coupons: {
    locationIds: [],
    action: 'add',
  },
  reports: {
    locationIds: [],
    action: 'lock',
  },
  companies: {
    locationIds: [],
    action: 'edit',
  },
};

const locationAdminDefaultAuthorizations = {
  home: {
    locationIds: [],
    action: 'hide',
  },
  menu: {
    locationIds: [],
    action: 'edit',
  },
  tiers: {
    locationIds: [],
    action: 'hide',
  },
  orders: {
    locationIds: [],
    action: 'edit',
  },
  locations: {
    locationIds: [],
    action: 'edit',
  },
  users: {
    locationIds: [],
    action: 'hide',
  },
  coupons: {
    locationIds: [],
    action: 'hide',
  },
  reports: {
    locationIds: [],
    action: 'hide',
  },
  companies: {
    locationIds: [],
    action: 'hide',
  },
};

const getAuthorizationLookupName = (resource, subResourceOrSmallTable, admin) => {
  if (getLocationIdOfAuthorizationOrAdmin('locations', admin)
    && (
      locationAdminLocationsKeyCoveredResources.includes(subResourceOrSmallTable)
      || locationAdminLocationsKeyCoveredResources.includes(resource)
    )
  ) return 'locations';
  return subPageResources.includes(resource)
    ? subPageResourceToAuthorizationResource[resource] : resource;
};

/**
 * Returns an action name like 'edit', 'view', 'add'.
 * Handles upgrades and downgrades of permissions.
 */
const figureOutPermittedActionName = (
  authorizations,
  authorizationLookupName,
  resource,
  subResourceOrSmallTable,
  attemptedAction,
  admin,
) => {
  const authorizationRecord = getAuthorizationRecord(
    authorizations,
    authorizationLookupName,
    admin,
  );
  const basePermittedAction = authorizationRecord.action;

  const locationId = getLocationIdFromAuthorizationsInAdmin(admin)
    || getLocationIdFromLocationInAdmin(admin);

  // Normal admins and higher
  if (!locationId) {
    const isCraverStaff = craverStaffAddress.test(admin.email);
    // So that our auto-generated normal admins during app creation still can add
    // e.g. locations. Does not include @craverapp.com addresses
    if (isCraverStaff && craverStaffFullPermissionResources.includes(resource)) return 'add';

    // Normal non-Craver staff admins should not be able to delete locations.
    // But they can if they have the 'add' permission.
    if (resource === 'locations' && !subResourceOrSmallTable && attemptedAction === 'delete') {
      if (basePermittedAction === 'edit') return 'view';
      if (basePermittedAction === 'add') return 'add';
    }

    if (resource === 'options' && authorizationLookupName === 'menu' && basePermittedAction === EDIT) return ADD;

    const deleteOrDuplicateCompany = resource === 'companies'
      && ['delete', 'add', 'deleteAsLocationAdmin'].includes(attemptedAction);
    // Non-Craver staff shouldn't have the power to delete or duplicate their company records,
    // which will probably interrupt their service
    if (!isCraverStaff && deleteOrDuplicateCompany) return 'view';

    return (basePermittedAction === 'edit' && resource !== 'menu' && subResourceOrSmallTable && !smallTables.includes(subResourceOrSmallTable))
      ? 'add'
      : basePermittedAction;
  }

  // Location admins
  if (locationAdminDuplicateDeleteResourceNames.includes(subResourceOrSmallTable)) return 'add';
  if (attemptedAction === 'deleteAsLocationAdmin') return 'view';
  if (['categories', 'taxes'].includes(resource) && !subResourceOrSmallTable && attemptedAction === EDIT) return VIEW;

  return basePermittedAction;
};

const getPermittedActionName = (admin, resource) => {
  const authorizations = getAuthorizations(admin);
  const authorizationLookupName = subPageResources.includes(resource) ? subPageResourceToAuthorizationResource[resource] : resource;
  const authorizationRecord = getAuthorizationRecord(
    authorizations,
    authorizationLookupName,
    admin,
  );
  return authorizationRecord.action;
};

// Handles if an authorization record is missing,
// like menu: { ... } is defined but not coupons: { ... }
const getAuthorizationRecord = (
  authorizations,
  authorizationLookupName,
  admin,
) => (
  authorizations[authorizationLookupName]
  || defaultAuthorizations(admin)[authorizationLookupName]
);

const getAuthorizations = admin => (
  admin
  && admin.authorizations
  && !isEmpty(admin.authorizations) ? admin.authorizations : defaultAuthorizations(admin)
);


/**
 * Resources in their own right whose authorizations are bound with the larger group they appear in.
 * E.g. authorization for'customizations' is covered by the authorization for 'menu'
 */
const subPageResources = ['categories', 'customizations', 'taxes', 'products', 'options', 'items'];

const subPageResourceToAuthorizationResource = {
  categories: 'menu',
  customizations: 'menu',
  taxes: 'menu',
  products: 'menu',
  options: 'menu',
  items: 'menu',
};

const locationAdminDuplicateDeleteResourceNames = ['zones', 'catering-zones'];

const locationAdminLocationsKeyCoveredResources = ['products', 'options', 'menu', 'locations', 'orders'];

export {
  checkIsAuthorized,
  getAuthorizations,
  getPermittedActionName,
  getLocationIdOfAuthorizationOrAdmin,
  LEVEL_NAMES,
  locationAdminDefaultAuthorizations,
  normalAdminDefaultAuthorizations,
  staffFullDefaultAuthorizations,
  subPageResources,
  subPageResourceToAuthorizationResource,
};
